import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Box, Typography, TextField, Button, CircularProgress, Grid } from "@mui/material";
import { useSnackbar } from "context/SnackbarContext";
import api from "services/api";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const WarehouseItemModal = ({ open, handleClose, item, onSuccess }) => {
  const [formData, setFormData] = useState({
    item_code: "",
    name: "",
    group: "",
  });
  const [loading, setLoading] = useState(false);
  const { selectedCompany } = useCompany();
  const showSnackbar = useSnackbar();
  const { t } = useLanguage();

  const isEditMode = item !== null;

  useEffect(() => {
    if (item) {
      setFormData({
        item_code: item.item_code || "",
        name: item.name || "",
        group: item.group || "",
      });
    } else {
      setFormData({ item_code: "", name: "", group: "" });
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = { ...formData, company_id: selectedCompany.id };
    const url = isEditMode ? `/api/warehouse/items/${item.id}/` : "/api/warehouse/items/create/";

    try {
      const response = isEditMode ? await api.put(url, payload) : await api.post(url, payload);
      if (response.data.success) {
        showSnackbar(
          isEditMode
            ? t("warehouseItemUpdatedSuccessfully")
            : t("warehouseItemCreatedSuccessfully"),
          "success"
        );
        onSuccess();
        handleClose();
      } else {
        showSnackbar(
          response.data.error || t("unknownErrorOccurred") || "An unknown error occurred",
          "error"
        );
      }
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || t("failedToSaveItem") || "Failed to save item.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2">
          {isEditMode ? t("editWarehouseItem") : t("addWarehouseItem")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="item_code"
                label={t("itemCode")}
                value={formData.item_code}
                onChange={handleChange}
                fullWidth
                required
                disabled={isEditMode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t("itemName")}
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="group"
                label={t("itemGroup")}
                value={formData.group}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={handleClose} sx={{ mr: 1 }}>
                {t("cancel")}
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : isEditMode ? (
                  t("saveChanges")
                ) : (
                  t("addItem")
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

WarehouseItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  item: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

WarehouseItemModal.defaultProps = {
  item: null,
};

export default WarehouseItemModal;
